import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HandCoins, Hourglass, Landmark, Wallet } from "lucide-react";
import { otherBalancesHint } from "./otherBalances";
import {
  BONUS_TYPE_LABELS,
  COMMISSION_ROUTES,
  COMMISSION_STATUS_LABELS,
  commissionListResponseSchema,
  commissionSummaryResponseSchema,
  formatPrice,
  type CommissionStatus,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Badge, type BadgeVariant } from "@/shared/ui/core";
import { EmptyState, SkeletonTable } from "@/shared/ui/ui";
import {
  DataTable,
  DataTableFrame,
  KpiCard,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { formatOrderDate } from "@/features/admin/orders/format";

const NO_PREFIX = { baseUrl: "" } as const;

const STATUS_VARIANTS: Record<CommissionStatus, BadgeVariant> = {
  pending: "warning",
  released: "info",
  paid: "success",
  rejected: "error",
  forfeited: "neutral",
};

/**
 * ═══════════════════════════════════════════════════════════════════
 * Mis comisiones — la misma página para el SOCIO y el FRANQUICIATARIO
 * (por eso vive en `features/panel/`, no en un panel concreto). Los
 * números salen de `/api/commissions/me`: cada quien ve LO SUYO, el
 * servidor no acepta otra cosa.
 *
 * Cada renglón trae su cuenta a la vista: base × % = monto — la regla
 * de "sé específico con las comisiones" (Anahí, 27-ago) aplicada al
 * dueño del dinero.
 * ═══════════════════════════════════════════════════════════════════
 */
export function MisComisionesPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o 20,
   * con CINCO por defecto. Vive en el estado y no como constante porque
   * ahora cambia en caliente; al cambiarlo se vuelve a la página 1, que es
   * lo único sensato — la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);

  const summary = useQuery({
    queryKey: ["me", "commissions", "summary"],
    queryFn: async () => {
      const data = await authFetch(
        COMMISSION_ROUTES.summary,
        commissionSummaryResponseSchema,
        NO_PREFIX,
      );
      return data.summary;
    },
  });

  const commissions = useQuery({
    queryKey: ["me", "commissions", page],
    queryFn: () =>
      authFetch(
        `${COMMISSION_ROUTES.mine}?page=${page}&perPage=${perPage}`,
        commissionListResponseSchema,
        NO_PREFIX,
      ),
    placeholderData: (previous) => previous,
  });

  const items = commissions.data?.items ?? [];
  const total = commissions.data?.total ?? 0;
  const resumen = summary.data;

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Mis comisiones")}
        description={t("Lo que tu red te ha generado, con la cuenta de cada bono.")}
      />

      {resumen && (
        <div className="mb-4 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
          <KpiCard
            icon={Hourglass}
            item="por-liberar"
            label={t("Por liberar")}
            value={formatPrice(resumen.pendingCents, resumen.currency)}
            hint={otherBalancesHint(resumen, "pendingCents")}
          />
          <KpiCard
            icon={Wallet}
            item="wallet"
            label={t("Liberado")}
            value={formatPrice(resumen.releasedCents, resumen.currency)}
            hint={otherBalancesHint(resumen, "releasedCents")}
          />
          <KpiCard
            icon={Landmark}
            item="pagado"
            label={t("Pagado")}
            value={formatPrice(resumen.paidCents, resumen.currency)}
            hint={otherBalancesHint(resumen, "paidCents")}
          />
        </div>
      )}

      <DataTableFrame aria-busy={commissions.isFetching}>
        {commissions.isPending ? (
          <div className="p-5">
            <SkeletonTable rows={5} columns={7} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<HandCoins className="h-5 w-5 text-ds-ink-3" />}
              title={t("Todavía sin comisiones")}
              description={t("Cuando alguien de tu red compre y su pago se valide, tu bono aparece aquí con su cuenta.")}
            />
          </div>
        ) : (
          <DataTable caption={t("Mis comisiones")}>
            <thead>
              <tr>
                <Th>{t("Bono")}</Th>
                <Th align="right">{t("Nivel")}</Th>
                <Th align="right">{t("Base")}</Th>
                <Th align="right">{t("%")}</Th>
                <Th align="right">{t("Comisión")}</Th>
                <Th>{t("Estado")}</Th>
                <Th align="right">{t("Fecha")}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((commission) => (
                <Tr key={commission.id}>
                  <Td className="font-medium">{BONUS_TYPE_LABELS[commission.bonusType]}</Td>
                  <Td align="right">{commission.level ?? "—"}</Td>
                  <Td align="right" className="font-mono text-[13px] text-ds-ink-3">
                    {formatPrice(commission.baseCents, commission.currency)}
                  </Td>
                  <Td align="right" className="font-mono text-[13px]">
                    × {commission.bpsApplied / 100}%
                  </Td>
                  <Td align="right" className="font-semibold text-ds-violet-pop">
                    {formatPrice(commission.amountCents, commission.currency)}
                  </Td>
                  <Td>
                    <Badge variant={STATUS_VARIANTS[commission.status]} size="sm" dot>
                      {t(COMMISSION_STATUS_LABELS[commission.status])}
                    </Badge>
                  </Td>
                  <Td align="right" className="text-ds-ink-3">
                    {formatOrderDate(commission.createdAt)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </DataTable>
        )}

        {total > 0 && (
          <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        )}
      </DataTableFrame>
    </>
  );
}
